import {Group, Paper, Popover, Stack, Text} from "@mantine/core";
import {useMemo, useState} from "react";
import type {UserLocation} from "../../types/swc.types.ts";
import type {WareCategory, WareVendorRow} from "./types";
import WareImage from "./WareImage.tsx";

const MAP_SIZE = 300;
const GALAXY_MIN = -500;
const GALAXY_MAX = 500;
const GALAXY_SPAN = GALAXY_MAX - GALAXY_MIN;
const DOT_HIT_SIZE = 18;

const priceFormat = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});

interface WareVendorMapProps {
    rows: WareVendorRow[];
    hoveredRowId: string | null;
    userLocation: UserLocation | null;
}

interface DotEntry {
    rowId: string;
    wareName: string;
    vendorName: string;
    price: number;
    image?: string;
    category: WareCategory;
}

interface DotGroup {
    id: string;
    x: number;
    y: number;
    left: number;
    top: number;
    rowIds: Set<string>;
    entries: DotEntry[];
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const toMapPoint = (x: number, y: number) => {
    const px = ((x - GALAXY_MIN) / GALAXY_SPAN) * MAP_SIZE;
    const py = ((GALAXY_MAX - y) / GALAXY_SPAN) * MAP_SIZE;
    return {
        left: clamp(px, 0, MAP_SIZE),
        top: clamp(py, 0, MAP_SIZE),
    };
};

const getVendorGalaxyPoint = (row: WareVendorRow): {x: number; y: number} | null => {
    const galaxy = row.location?.coordinates?.galaxy;
    if (!galaxy) return null;
    if (typeof galaxy.x !== 'number' || typeof galaxy.y !== 'number') return null;
    return {x: galaxy.x, y: galaxy.y};
};

const getUserGalaxyPoint = (userLocation: UserLocation | null): {x: number; y: number} | null => {
    const galaxy = userLocation?.coordinates?.galaxy?.attributes;
    if (!galaxy) return null;
    if (typeof galaxy.x !== 'number' || typeof galaxy.y !== 'number') return null;
    return {x: galaxy.x, y: galaxy.y};
};

const buildDotGroups = (rows: WareVendorRow[]): DotGroup[] => {
    const groups = new Map<string, DotGroup>();

    for (const row of rows) {
        const point = getVendorGalaxyPoint(row);
        if (!point) continue;

        const key = `${point.x}:${point.y}`;
        const current = groups.get(key) ?? {
            id: key,
            x: point.x,
            y: point.y,
            ...toMapPoint(point.x, point.y),
            rowIds: new Set<string>(),
            entries: [],
        };

        current.rowIds.add(row.id);
        current.entries.push({
            rowId: row.id,
            wareName: row.entityName || row.name,
            vendorName: row.vendorName,
            price: row.price,
            image: row.images?.small,
            category: row.category,
        });

        groups.set(key, current);
    }

    return Array.from(groups.values());
};

export default function WareVendorMap({rows, hoveredRowId, userLocation}: WareVendorMapProps) {
    const [hoveredDotId, setHoveredDotId] = useState<string | null>(null);

    const dotGroups = useMemo(() => buildDotGroups(rows), [rows]);

    const userDot = useMemo(() => {
        const point = getUserGalaxyPoint(userLocation);
        if (!point) return null;
        return {...toMapPoint(point.x, point.y), x: point.x, y: point.y};
    }, [userLocation]);

    return (
        <Paper withBorder radius="md" p="xs">
            <div
                style={{
                    width: MAP_SIZE,
                    height: MAP_SIZE,
                    position: 'relative',
                    borderRadius: 8,
                    overflow: 'hidden',
                    backgroundImage: 'url(https://assets.swc-forge.com/map-grey.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid rgba(255,255,255,0.08)',
                }}
            >
                {dotGroups.map((group) => {
                    const isHovered = hoveredDotId === group.id || (hoveredRowId !== null && group.rowIds.has(hoveredRowId));
                    return (
                        <Popover
                            key={group.id}
                            opened={hoveredDotId === group.id}
                            position="top"
                            withArrow
                            shadow="md"
                            offset={8}
                            withinPortal
                        >
                            <Popover.Target>
                                <div
                                    title={`${group.x}, ${group.y}`}
                                    onMouseEnter={() => setHoveredDotId(group.id)}
                                    onMouseLeave={() => setHoveredDotId((current) => (current === group.id ? null : current))}
                                    style={{
                                        position: 'absolute',
                                        left: group.left,
                                        top: group.top,
                                        transform: 'translate(-50%, -50%)',
                                        width: DOT_HIT_SIZE,
                                        height: DOT_HIT_SIZE,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        zIndex: isHovered ? 3 : 2,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: isHovered ? 10 : 6,
                                            height: isHovered ? 10 : 6,
                                            borderRadius: '50%',
                                            background: isHovered ? '#facc15' : '#ef4444',
                                            border: isHovered ? '2px solid #111827' : '1px solid rgba(17, 24, 39, 0.7)',
                                            boxShadow: isHovered ? '0 0 0 2px rgba(250, 204, 21, 0.25)' : 'none',
                                            pointerEvents: 'none',
                                        }}
                                    />
                                </div>
                            </Popover.Target>
                            <Popover.Dropdown
                                p="xs"
                                onMouseEnter={() => setHoveredDotId(group.id)}
                                onMouseLeave={() => setHoveredDotId((current) => (current === group.id ? null : current))}
                                style={{maxWidth: 340}}
                            >
                                <Stack gap="xs">
                                    {group.entries.map((entry, index) => (
                                        <Group key={`${entry.rowId}:${index}`} gap="xs" wrap="nowrap" align="flex-start">
                                            {entry.image ? (
                                                <WareImage src={entry.image} alt={entry.wareName} size={56} category={entry.category} radius="sm" />
                                            ) : null}
                                            <Stack gap={2}>
                                                <Text fw={600} size="sm">{entry.wareName}</Text>
                                                <Text size="sm">{entry.vendorName}</Text>
                                                <Text size="sm" c="dimmed">{priceFormat.format(entry.price)}</Text>
                                            </Stack>
                                        </Group>
                                    ))}
                                </Stack>
                            </Popover.Dropdown>
                        </Popover>
                    );
                })}

                {userDot ? (
                    <div
                        title={`Your location (${userDot.x}, ${userDot.y})`}
                        style={{
                            position: 'absolute',
                            left: userDot.left,
                            top: userDot.top,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            transform: 'translate(-50%, -50%)',
                            background: '#a855f7',
                            border: '1px solid #ffffff',
                            boxShadow: '0 0 0 2px rgba(168, 85, 247, 0.25)',
                            zIndex: 4,
                            pointerEvents: 'none',
                        }}
                    />
                ) : null}
            </div>
        </Paper>
    );
}
